import { HotelsAvailabilitySelectors } from "@b2bportal/core-hotels";
import { useSelector } from "react-redux";
import {
  type Intl_ListFormatOptions,
  useI18nContext,
  useI18nList,
} from "@hopper-b2b/i18n";

// For Deeplinks sometimes the search is not available immeaditely,
// so we need to have a default search to avoid errors
const defaultSearch = {
  adults: 1,
  children: [],
};

export interface GuestCounts {
  adultsCount: number;
  childrenCount: number;
  infantCount: number;
}

export interface UseHotelGuestCountReturn {
  counts: GuestCounts & { totalCount: number };
  formattedGuestCountString: (formatOptions?: Intl_ListFormatOptions) => string;
}

export const getChildrenBreakdown = (children: number[]) => {
  const counts = { infantCount: 0, childrenCount: 0 };
  for (const num of children) {
    if (num <= 1) {
      counts.infantCount++;
    } else {
      counts.childrenCount++;
    }
  }
  return counts;
};

export const useHotelGuestCount = (
  guestCounts?: GuestCounts
): UseHotelGuestCountReturn => {
  const { t } = useI18nContext();
  const { formatList } = useI18nList();
  const { children, adults: adultsCount } =
    useSelector(HotelsAvailabilitySelectors.getSearchFormGuests) ||
    defaultSearch;

  let { childrenCount, infantCount } = getChildrenBreakdown(children);
  if (guestCounts) {
    childrenCount = guestCounts.childrenCount;
    infantCount = guestCounts.infantCount;
  }

  const guestCountString = [
    t("passengersInformation.adult", {
      count: guestCounts?.adultsCount ?? adultsCount,
    }),
  ];

  if (childrenCount) {
    guestCountString.push(
      t("passengersInformation.child", {
        count: childrenCount,
      })
    );
  }

  if (infantCount) {
    guestCountString.push(
      t("passengersInformation.infant", {
        count: infantCount,
      })
    );
  }

  return {
    counts: {
      adultsCount: guestCounts?.adultsCount ?? adultsCount,
      childrenCount,
      infantCount,
      totalCount:
        infantCount + childrenCount + (guestCounts?.adultsCount ?? adultsCount),
    },
    formattedGuestCountString: (formatOptions = { type: "conjunction" }) =>
      formatList(guestCountString, formatOptions),
  };
};
